import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Checkbox from "components/form/checkbox/Checkbox";
import Sheet from "components/sheet/Sheet";
import Input from "components/form/input/Input";
import { Select as SelectNew } from "components/select/";
import { AccountingReferenceType, HouseAccountType } from "../../context/contextTypes";
import { useAppSelector } from "hooks/redux";
import { useCustomerContext } from "../../context/CustomerContext";
import { capitalize } from "helpers/Helpers";

type NewCustomerHouseAccountModalProps = {
  customerId: number;
  accountingReferenceTypes: AccountingReferenceType[] | null;
  isVisible: boolean;
  onClose: () => void;
  onOk: (houseAccount: HouseAccountType, isValid: boolean) => void;
};

export default function NewCustomerHouseAccountModal(props: NewCustomerHouseAccountModalProps) {
  const { customerId, accountingReferenceTypes, isVisible, onClose } = props;
  const { facilityStore } = useAppSelector(store => store);
  const { state } = useCustomerContext();
  const { t } = useTranslation();
  const { Option } = SelectNew;

  const minimumDayInput = 1;
  const maximumDayInput = 31;

  const [houseAccount, setHouseAccount] = useState<HouseAccountType & { limit_is_valid: boolean }>({
    account_number: "",
    accounting_reference_id: 0,
    balance: 0,
    client_id: undefined,
    currency: "",
    customer_id: customerId,
    facility_id: undefined,
    global: false,
    id: undefined,
    limit: "",
    title: "",
    limit_is_valid: false,
    default_payment_method_id: null,
    send_statement: !!facilityStore?.teeSheetSettings?.accounts_send_statement,
    auto_pay_balance: !!facilityStore?.teeSheetSettings?.accounts_auto_pay_balance,
    auto_pay_anchor_date: facilityStore?.teeSheetSettings?.accounts_auto_pay_anchor_date,
  });

  useEffect(() => {
    setHouseAccount(prevState => ({
      ...prevState,
      send_statement: !!facilityStore?.teeSheetSettings?.accounts_send_statement,
      auto_pay_balance: !!facilityStore?.teeSheetSettings?.accounts_auto_pay_balance,
      auto_pay_anchor_date: facilityStore?.teeSheetSettings?.accounts_auto_pay_anchor_date,
    }));
  }, [facilityStore?.teeSheetSettings]);

  function handleCloseModal() {
    setHouseAccount(prevState => ({
      ...prevState,
      global: false,
      accounting_reference_id: 0,
      account_number: "",
      limit: "",
      title: "",
    }));

    onClose();
  }

  function handleChange(event: any) {
    const id = event?.target?.id;
    const value = event?.target?.value;

    if (id === undefined || value === undefined) {
      return;
    }

    setHouseAccount(prevState => ({ ...prevState, [id]: value }));
  }

  function handleNumberChange(event: any, propertyToChange: string, validFlagToChange: string) {
    const value = event?.target?.value;

    if (value === undefined) {
      return;
    }

    const valueCharacters = value.split("");
    setHouseAccount(prevState => ({
      ...prevState,
      [propertyToChange]: value,
      [validFlagToChange]: valueCharacters.length > 0 && valueCharacters.every((v: any) => !Number.isNaN(Number(v))),
    }));
  }

  function handleOnOk(houseAccount: HouseAccountType, isValid: boolean) {
    props.onOk(houseAccount, isValid);
    handleCloseModal();
  }

  return (
    <Sheet
      open={isVisible}
      size="medium"
      height="flexible"
      closable
      title={t("New Account")}
      onCancel={() => handleCloseModal()}
      onOk={() => handleOnOk(houseAccount, houseAccount.limit_is_valid)}
      cancelText={t("Cancel")}
      okText={t("Create Account")}
      okDisabled={houseAccount.title === "" || !houseAccount.limit_is_valid ? true : false}
      overflow
    >
      <Form>
        <FormLayout>
          <FormLayout.Group>
            <Input
              value={houseAccount.title}
              label={t("Title")}
              id="title"
              placeholder={t("Title")}
              onChange={handleChange}
            />
            <Input
              value={houseAccount.account_number}
              id="account_number"
              label={t("Account Number")}
              onChange={handleChange}
              placeholder={t("Account Number")}
              helpText={t("Leave empty to auto generate a number")}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <Input
              value={houseAccount.limit}
              label={t("Limit")}
              id={Number(houseAccount.limit) > 0 && !houseAccount.limit_is_valid ? "inputError" : ""}
              onChange={(e: any) => handleNumberChange(e, "limit", "limit_is_valid")}
              placeholder={t("Limit")}
            />
            {accountingReferenceTypes && (
              <SelectNew
                label={t("Accounting Reference")}
                defaultValue={facilityStore.teeSheetSettings?.accounts_accounting_reference_id}
                onChange={(value: number) => setHouseAccount({ ...houseAccount, accounting_reference_id: value })}
                placeholder={t("Select Accounting Reference")}
              >
                {accountingReferenceTypes.map((type, index: number) => {
                  return (
                    <Option key={index} value={type.value} name={type.label}>
                      <span>{type.label}</span>
                    </Option>
                  );
                })}
              </SelectNew>
            )}
          </FormLayout.Group>

          <FormLayout.Group>
            <Checkbox
              id="global"
              size="medium"
              value={houseAccount.global}
              checked={houseAccount.global}
              onChange={({ target }) =>
                setHouseAccount({ ...houseAccount, [target.id]: target.value === "true" ? false : true })
              }
              label={t("This account can be used across all courses")}
            />
            <Checkbox
              id="send_statement"
              size="medium"
              value={houseAccount.send_statement}
              checked={houseAccount.send_statement}
              onChange={({ target }) =>
                setHouseAccount({ ...houseAccount, [target.id]: target.value === "true" ? false : true })
              }
              label={"Send Monthly Statements"}
            />
            <Checkbox
              id="auto_pay_balance"
              size="medium"
              value={houseAccount.auto_pay_balance}
              checked={houseAccount.auto_pay_balance}
              onChange={({ target }) =>
                setHouseAccount({ ...houseAccount, [target.id]: target.value === "true" ? false : true })
              }
              label={"Auto Pay Balance"}
            />
          </FormLayout.Group>

          {houseAccount?.auto_pay_balance ? (
            <FormLayout.Group>
              <Input
                label={t("Auto Pay Date")}
                type="number"
                id="auto_pay_anchor_date"
                value={
                  houseAccount?.auto_pay_anchor_date < minimumDayInput
                    ? ""
                    : houseAccount?.auto_pay_anchor_date > maximumDayInput
                    ? maximumDayInput
                    : houseAccount?.auto_pay_anchor_date
                }
                onChange={e => setHouseAccount(prev => ({ ...prev, auto_pay_anchor_date: Number(e.target?.value) }))}
                prefix={t("Day of Month")}
                error={houseAccount?.auto_pay_anchor_date === 0}
              />

              {state?.paymentMethods?.length > 0 ? (
                <SelectNew
                  label={t("Payment Methods")}
                  defaultValue={state?.paymentMethods[0].id}
                  onChange={(value: number) => setHouseAccount({ ...houseAccount, default_payment_method_id: value })}
                >
                  {state?.paymentMethods?.map((method, index: number) => {
                    return (
                      <Option key={index} value={method.id} name={method.type}>
                        {capitalize(method?.brand)} •••• •••• •••• {method?.last4}
                      </Option>
                    );
                  })}
                </SelectNew>
              ) : null}
            </FormLayout.Group>
          ) : null}
        </FormLayout>
      </Form>
    </Sheet>
  );
}
